import React, { useState } from 'react';
import { Container, Card, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';

interface PreferencesProps {
  user: any;
  onSave: (preferences: any) => void;
}

const Preferences: React.FC<PreferencesProps> = ({ user, onSave }) => {
  const [preferences, setPreferences] = useState({
    notifications: {
      emailNewMatches: true,
      textPriceDrops: true,
      weeklyInsights: false
    },
    search: {
      minPrice: 450000,
      maxPrice: 650000,
      minBedrooms: 3,
      minBathrooms: 2,
      minSqft: 2000
    },
    features: ['Pool', '3-car garage', 'Updated kitchen'],
    areas: ['Scottsdale', 'Paradise Valley']
  });

  const [newFeature, setNewFeature] = useState('');
  const [newArea, setNewArea] = useState('');

  const handleSave = () => {
    onSave(preferences);
    alert('Preferences saved successfully!');
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setPreferences({
        ...preferences,
        features: [...preferences.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setPreferences({
      ...preferences,
      features: preferences.features.filter((_, i) => i !== index)
    });
  };

  const addArea = () => {
    if (newArea.trim()) {
      setPreferences({
        ...preferences,
        areas: [...preferences.areas, newArea.trim()]
      });
      setNewArea('');
    }
  };

  const removeArea = (index: number) => {
    setPreferences({
      ...preferences,
      areas: preferences.areas.filter((_, i) => i !== index)
    });
  };

  return (
    <Container fluid className="preferences p-4">
      <h2 className="mb-4">⚙️ Search Preferences</h2>

      {/* Notification Settings */}
      <Card className="mb-4">
        <Card.Body>
          <h5>Notification Settings</h5>
          <Form>
            <Form.Check
              type="checkbox"
              id="email-matches"
              label="Email me about new matches (Daily)"
              checked={preferences.notifications.emailNewMatches}
              onChange={(e) => setPreferences({
                ...preferences,
                notifications: {
                  ...preferences.notifications,
                  emailNewMatches: e.target.checked
                }
              })}
              className="mb-2"
            />
            <Form.Check
              type="checkbox"
              id="text-drops"
              label="Text alerts for price drops"
              checked={preferences.notifications.textPriceDrops}
              onChange={(e) => setPreferences({
                ...preferences,
                notifications: {
                  ...preferences.notifications,
                  textPriceDrops: e.target.checked
                }
              })}
              className="mb-2"
            />
            <Form.Check
              type="checkbox"
              id="weekly-insights"
              label="Theta's weekly market insights"
              checked={preferences.notifications.weeklyInsights}
              onChange={(e) => setPreferences({
                ...preferences,
                notifications: {
                  ...preferences.notifications,
                  weeklyInsights: e.target.checked
                }
              })}
            />
          </Form>
        </Card.Body>
      </Card>

      {/* Search Criteria */}
      <Card className="mb-4">
        <Card.Body>
          <h5>Search Criteria</h5>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Price</Form.Label>
                  <Form.Control
                    type="number"
                    value={preferences.search.minPrice}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      search: {
                        ...preferences.search,
                        minPrice: parseInt(e.target.value)
                      }
                    })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Maximum Price</Form.Label>
                  <Form.Control
                    type="number"
                    value={preferences.search.maxPrice}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      search: {
                        ...preferences.search,
                        maxPrice: parseInt(e.target.value)
                      }
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Min Bedrooms</Form.Label>
                  <Form.Select
                    value={preferences.search.minBedrooms}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      search: {
                        ...preferences.search,
                        minBedrooms: parseInt(e.target.value)
                      }
                    })}
                  >
                    <option value={1}>1+</option>
                    <option value={2}>2+</option>
                    <option value={3}>3+</option>
                    <option value={4}>4+</option>
                    <option value={5}>5+</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Min Bathrooms</Form.Label>
                  <Form.Select
                    value={preferences.search.minBathrooms}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      search: {
                        ...preferences.search,
                        minBathrooms: parseInt(e.target.value)
                      }
                    })}
                  >
                    <option value={1}>1+</option>
                    <option value={2}>2+</option>
                    <option value={3}>3+</option>
                    <option value={4}>4+</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Min Square Feet</Form.Label>
                  <Form.Control
                    type="number"
                    value={preferences.search.minSqft}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      search: {
                        ...preferences.search,
                        minSqft: parseInt(e.target.value)
                      }
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Must-Have Features */}
      <Card className="mb-4">
        <Card.Body>
          <h5>Must-Have Features</h5>
          <div className="mb-3">
            {preferences.features.map((feature, index) => (
              <Badge key={index} bg="primary" className="me-2 mb-2">
                {feature}
                <FontAwesomeIcon
                  icon={faTimes}
                  className="ms-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => removeFeature(index)}
                />
              </Badge>
            ))}
          </div>
          <div className="d-flex">
            <Form.Control
              type="text"
              placeholder="Add a feature..."
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addFeature()}
            />
            <Button variant="primary" className="ms-2" onClick={addFeature}>
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Preferred Areas */}
      <Card className="mb-4">
        <Card.Body>
          <h5>Preferred Areas</h5>
          <div className="mb-3">
            {preferences.areas.map((area, index) => (
              <Badge key={index} bg="secondary" className="me-2 mb-2">
                {area} ✓
                <FontAwesomeIcon
                  icon={faTimes}
                  className="ms-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => removeArea(index)}
                />
              </Badge>
            ))}
          </div>
          <div className="d-flex">
            <Form.Control
              type="text"
              placeholder="Add an area..."
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addArea()}
            />
            <Button variant="secondary" className="ms-2" onClick={addArea}>
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Button variant="success" size="lg" onClick={handleSave}>
        <FontAwesomeIcon icon={faSave} className="me-2" />
        Save Preferences
      </Button>
    </Container>
  );
};

export default Preferences;